import React, { useState, useRef, useEffect, useMemo } from "react";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import { Download, ChevronDown } from "lucide-react";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import ContractsDetails from "./tabs/ContractsDetails";
import LanesDetails from "./tabs/LanesDetails";
import RoutesDetails from "./tabs/RoutesDetails";
import RatesDetails from "./tabs/RatesDetails";
import DraftsContract from "./templates/DraftsContract";
import NewContract from "./templates/NewContract";
import Cookies from "js-cookie";
import BulkUploadExcelModal from "./Modal/BulkUploadExcelModal";
import { ContractDetailsContent } from "./components/ContractDetailsContent";
import ContractDropdownContent from "./components/ContractDropdownContent";
import { contractApi } from "./api/contractApi";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";

import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  addTab,
  removeTab,
  setActiveTab,
} from "../../../redux/slices/contractsSlice";
import SearchResultsContainer from "./search/SearchResultsContainer";
import { exportContractsExcel } from "./api/exportFileApi";

const moduleName = "contracts";

const ContractTabHandler = () => {
  const dispatch = useDispatch();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentSearchKeyword, setCurrentSearchKeyword] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const defaultContractFilters = {
    offices: [],
    statuses: [],
    query: "",
  };
  const [contractFilters, setContractFilters] = useState(defaultContractFilters);
  const [refreshingOffices, setRefreshingOffices] = useState([]);
  const [contractCount, setContractCount] = useState(0);

  const { contracts, dynamicTabs, activeTab } = useSelector(
    (state) => state.contracts
  );
  const tabs = useMemo(
    () => [...contracts, ...dynamicTabs],
    [contracts, dynamicTabs]
  );

  const defaultTabsCount = contracts.length;
  const officeCodesCookie = Cookies.get("officeCodes");
  const officeCodes = officeCodesCookie ? officeCodesCookie.split(",") : [];
  const officeCode = officeCodes[0];
  const token = Cookies.get("authToken");
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);


  const defaultTabContents = {
    contracts: (
      <ContractsDetails
        key="contracts"
        officeCode={officeCode}
        onContractsFormatted={setContractCount}
      />
    ),
    lanes: <LanesDetails key="lanes" officeCode={officeCode} />,
    routes: <RoutesDetails key="routes" officeCode={officeCode} />,
    rates: <RatesDetails key="rates" officeCode={officeCode} />,
    drafts: <DraftsContract key="drafts" />,
  };

  // const handleAddTab = () => {
  //   dispatch(addTab({ module: moduleName }));
  // };

  useEffect(() => {
    const active = tabs[activeTab];

    if (active && active.type === "search" && active.searchTerm) {
      setCurrentSearchKeyword(active.searchTerm);
    } else {
      setCurrentSearchKeyword("");
    }
  }, [activeTab, tabs]);

  const handleExportContracts = async () => {
    const searchRequest = {
      query: currentSearchKeyword || "",
      status: null,
    };

    try {
      await exportContractsExcel(token, officeCodes, searchRequest, 0, 40000);
    } catch (e) {
      alert("Failed to export contracts. Please try again.");
    } finally {
      setIsOpen(false);
    }
  };

  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ module: moduleName, tabIndex: index }));
  };

  const handleSearch = () => {
    const keyword = searchKeyword.trim();
    if (!keyword) return;

    dispatch(
      addTab({
        module: moduleName,
        tabData: {
          name: `Search: ${keyword}`,
          type: "search",
          searchTerm: keyword,
          originTabName: tabs[activeTab].name,
        },
      })
    );
    setSearchKeyword("");
  };

  // 处理回车键
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validTabNames = ["Lanes", "Rates", "Routes", "Contracts"];
  const isMainTab = validTabNames.includes(tabs[activeTab]?.name);
  const handleContractFiltersApply = (appliedFilters) => {
    if (!appliedFilters) {
      setContractFilters({ ...defaultContractFilters });
      return;
    }

    setContractFilters(appliedFilters);

    const selectedOffices =
      appliedFilters.offices?.length > 0
        ? appliedFilters.offices
        : contractFilters.offices?.length > 0
        ? contractFilters.offices
        : officeCode
        ? [officeCode]
        : [];

    if (!selectedOffices.length) return;

    const officesToRefresh = selectedOffices.filter(
      (oc) => !refreshingOffices.includes(oc)
    );
    if (!officesToRefresh.length) return;

    setRefreshingOffices((prev) => [...new Set([...prev, ...officesToRefresh])]);

    officesToRefresh.forEach((oc) => {
      contractApi
        .refreshRateShoppingGraph(oc, today)
        .then((res) => {
          notify.success({
            title: `Refresh complete (${oc})`,
          });
        })
        .catch((err) => {
          console.error("Failed to refresh rate shopping graph:", err);
        })
        .finally(() => {
          setRefreshingOffices((prev) => prev.filter((o) => o !== oc));
        });
    });
  };

  return (
    <div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            text="Refresh Cache"
            backgroundColor="white"
            textColor="black"
            contentComponent={
            <ContractDropdownContent
              filters={contractFilters}
              setFilters={setContractFilters}
              offices={officeCodes}
              onApply={handleContractFiltersApply}
              operationDate={today}
              refreshingOffices={refreshingOffices}
            />
            }
          />
          <SearchBox
            value={searchKeyword}
            onChange={setSearchKeyword}
            onSearch={handleSearch}
            placeholder={isMainTab ? `Search ${tabs[activeTab].name}` : "Search Contracts"}
          />

          <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
            {contractCount}{" "}
            {contractCount === 1 ? "Contract" : "Contracts"}
          </span>
          {/* <div className="text-gray-500 text-fluid-sm px-1"><RefreshCw size={13}/></div> */}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center hover:text-[#1b2c7a] group">
            <Download className="text-custom-bg-blue" size={18} />
            <a
              href="/templates/Contracts_Upload_Template.xlsx"
              download
              className="text-fluid-sm text-custom-bg-blue underline font-medium ml-1 hover:text-[#1b2c7a]"
            >
              Template
            </a>
          </div>

          <div className="relative" ref={dropdownRef}>
            <Button
              className="bg-custom-bg-blue text-white"
              onClick={() => setIsOpen((prev) => !prev)}
              text={
                <div className="flex items-center gap-2">
                  <span>Bulk Contracts</span>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${
                      isOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </div>
              }
            />

            {isOpen && (
              <div className="absolute right-0 mt-fluid-1 min-w-[10rem] bg-white border border-gray-200 rounded-xl shadow-lg z-[401] animate-in fade-in-0 zoom-in-95">
                <div className="flex flex-col p-fluid-1.5">
                  <button
                    onClick={() => {
                      setShowUploadModal(true);
                      setIsOpen(false);
                    }}
                    className="px-fluid-2 py-fluid-1.5 text-fluid-xs sm:text-fluid-sm text-gray-800 hover:bg-gray-100 rounded-md text-left"
                  >
                    Import Contracts from Excel (.xlsx)
                  </button>

                  <button
                    onClick={handleExportContracts}
                    className="px-fluid-2 py-fluid-1.5 text-fluid-xs sm:text-fluid-sm text-gray-800 hover:bg-gray-100 rounded-md text-left"
                  >
                    Export Contracts to Excel (.xlsx)
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
        <Tabs
          moduleName={moduleName}
          tabs={tabs.map((tab) => tab.name)}
          tabContents={tabs.map((tab) => {
            if (tab.type === "search") {
              return (
                <SearchResultsContainer
                  key={tab.id}
                  searchTerm={tab.searchTerm}
                  activeTabData={tab.originTabName}
                  officeCode={officeCode}
                />
              );
            }
            if (tab.type === "contractDetails") {
                            return (
                <ContractDetailsContent
                  key={tab.id}
                  tabId={tab.id}
                  contractData={tab.contractData}
                  officeCode={tab.contractData.officeCode}
                  expandedDataKey={tab.expandedDataKey}
                />
              );
            }
            return (
              defaultTabContents[tab.id] || (
                <NewContract
                  key={tab.id}
                  title={tab.name}
                  tabId={tab.id}
                  isDynamic={tab.isDynamic}
                />
              )
            );
          })}
          activeTab={activeTab}
          onTabChange={(index) =>
            dispatch(setActiveTab({ module: moduleName, index }))
          }
          isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}
          aria-label="Contract tabs"
        />
      </div>
      {showUploadModal && (
        <BulkUploadExcelModal
          onClose={() => setShowUploadModal(false)}
          setUploadedFiles={setUploadedFiles}
          uploadedFiles={uploadedFiles}
          officeCode={officeCode}
        />
      )}
    </div>
  );
};

export default ContractTabHandler;
